/**
 * Asset Mgr board (design spec 2026-08-16, §8) — the post-closing surveillance
 * desk for one asset manager's portfolio.
 *
 * Every number on this page comes out of `GET /dashboards/asset`. The panel
 * shapes the service ships (backend/app/services/dashboards/asset.py) are
 * per-panel wire shapes rather than the kit's generic `DashboardPanelData`
 * union, so the wire contract this page reads is declared here explicitly and
 * `readPanel` is used in place of the kit's `getPanel`.
 *
 * Two panels of the reference layout have no counterpart in the payload and
 * are rendered as honest empty states rather than filled with derived
 * approximations: the original-vs-current comparison in the inspector, and the
 * inspector's audit trail. A board that guesses those numbers is worse than one
 * that says it does not have them.
 */
import { useMemo, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import clsx from 'clsx'
import {
  Activity,
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Building2,
  CalendarClock,
  ClipboardList,
  Columns3,
  ExternalLink,
  FileText,
  Inbox,
  Layers,
  Mail,
  Minus,
  PanelRightClose,
  PanelRightOpen,
  Phone,
  Printer,
  Search,
  ShieldAlert,
  SlidersHorizontal,
  StickyNote,
  TrendingUp,
  X,
} from 'lucide-react'
import {
  GenericSortHeader,
  dealTableHeadRowClass,
  dealTableRowClass,
  useGenericTableSort,
} from '@/components/shared/DealTable'
import {
  AttentionTable,
  BookUniverseLine,
  DASHBOARD_REFETCH_MS,
  DashboardGrid,
  DashboardShell,
  DashboardTabs,
  FigureValue,
  GridCell,
  KpiStrip,
  NO_VALUE,
  Panel,
  displayFigure,
  figureToneClass,
  useDashboardView,
  useDrillDown,
} from '@/components/dashboards'
import type { DashboardTab } from '@/components/dashboards'
import { CHART_VIZ_TRACK, chartPartOfWholeColor } from '@/config/chartTheme'
import api from '@/services/api'
import type {
  AttentionPanelData,
  BookUniverse,
  DashboardPayload,
  DrillDescriptor,
  Figure,
  FigureFormat,
  FigureTone,
  TilesPanelData,
} from '@/types/dashboards'
import { fmtDate } from './assetBoardCopy'

// ── Wire contract ───────────────────────────────────────────────────────────

interface ScopePanel {
  assetManagerEmail: string | null
  /** True when the board is showing the assets no manager owns. */
  unassigned?: boolean
  /** True when the board is showing the whole book — asked for, or because
   *  the signed-in reader manages no assets of their own. */
  desk?: boolean
  assets: number
  /** Funded loans in the active book. The header states it beside the
   *  monitored count so "0 monitored" and a tile reading the funded book are
   *  one story, not two numbers (stress test EXEC-15). */
  fundedLoans?: number
}

/** Header clause for funded loans that carry no monitoring record yet. */
function unmonitoredClause(scope: ScopePanel): string {
  const unmonitored = (scope.fundedLoans ?? 0) - scope.assets
  if (!scope.desk || unmonitored <= 0) return ''
  return ` · ${unmonitored} funded loan${unmonitored === 1 ? '' : 's'} not yet monitored`
}

/** What `?manager=` reads as "the whole book". */
const DESK_SCOPE = '__desk__'


interface RosterEntry {
  assetManagerEmail: string | null
  /** What to send back as `?manager=` to open this row.
   *
   *  A portfolio no manager owns cannot be addressed by its email — there is
   *  none, and an empty string is what the route reads as "no scope given",
   *  which resolves to the caller's own portfolio. So the server names the
   *  sentinel and the page sends it back verbatim rather than encoding the
   *  null case itself. Optional: a board built before the sentinel landed
   *  ships only the email. */
  scopeValue?: string
  assets: number
  elevated: number
  drill?: DrillDescriptor
}

interface TierEntry {
  tier: string
  label: string
  count: Figure
  balance: Figure
}

interface PortfolioEntry {
  id: string
  deal: string | null
  loanId: string | null
  amount: number | null
  tier: string | null
  riskRating: number | null
  riskDirection: string | null
  maturityOn: string | null
  refinanceOutlook: string | null
  reportingStatus: string | null
  nextAction: string | null
  nextMilestoneAt: string | null
  daysToMilestone: number | null
  href?: string | null
  drill?: DrillDescriptor
}

interface CovenantEntry {
  id: string
  deal: string | null
  loanId: string | null
  result: string | null
  testDate: string | null
  actualValue: number | null
  threshold: number | null
  cureDeadline: string | null
  href?: string | null
  drill?: DrillDescriptor
}

interface RequestTypeEntry {
  requestType: string
  label: string
  count: Figure
  amount: Figure
}

interface RequestEntry {
  id: string
  deal: string | null
  loanId: string | null
  requestType: string
  amount: number | null
  submittedAt: string | null
  dueAt: string | null
  daysToDue: number | null
  owner: string | null
  href?: string | null
  drill?: DrillDescriptor
}

interface MaturityBucket {
  key: string
  label: string
  count: Figure
  balance: Figure
}

interface ReportingEntry {
  id: string
  deal: string | null
  loanId: string | null
  daysLate: number | null
  lastUpdateAt: string | null
  nextAction: string | null
  href?: string | null
  drill?: DrillDescriptor
}

interface CovenantPanel {
  summary: Figure[]
  passRate?: Figure
  rows: CovenantEntry[]
}

interface RequestsPanel {
  byType: RequestTypeEntry[]
  rows: RequestEntry[]
}

interface MovementRow {
  key: string
  label: string
  up: Figure
  flat: Figure
  down: Figure
}

interface PerformanceMovementPanel {
  columns: { key: string; label: string }[]
  rows: MovementRow[]
  deterioratingExposure: Figure
}

interface MaturityPanel {
  buckets: MaturityBucket[]
  maturityNotRecorded?: Figure
  outlook: Figure[]
}

interface ReportingPanel {
  onTimeRate?: Figure
  rows: ReportingEntry[]
}

/** The server-ranked cross-source action list (asset.attention_panel).
 *  Same wire shape as the CEO and Legal boards' attention rows, because
 *  `common.attention_row` builds all three. */
interface AttentionRow {
  source: string
  title: string
  detail: string | null
  amount: number | null
  daysToDeadline: number | null
  severity: string | null
  action: string
  score: number
  drill: DrillDescriptor | null
  href: string | null
}

interface AttentionPanel {
  rows: AttentionRow[]
}

/**
 * The board document keys panels by name with a per-panel shape, so the kit's
 * `getPanel` (constrained to the generic panel union) cannot type them.
 */
function readPanel<T>(panels: Record<string, unknown>, key: string): T | null {
  const value = panels[key]
  return value ? (value as T) : null
}

// ── Vocabulary mirrored from the service ────────────────────────────────────

/** Tiers above routine surveillance — asset.py:ELEVATED_TIERS. */
const ELEVATED_TIERS = new Set(['heightened', 'watchlist', 'modification', 'workout'])

/** Only these tiers are tone-coded, per the reference layout. */
const TIER_TONE: Record<string, 'warning' | 'negative'> = {
  heightened: 'warning',
  watchlist: 'warning',
  workout: 'negative',
}

const COVENANT_TONE: Record<string, 'positive' | 'warning' | 'negative'> = {
  pass: 'positive',
  near: 'warning',
  watch: 'warning',
  breach: 'negative',
}

/** Worst-first, so a loan with several flagged tests reports its worst one. */
const COVENANT_SEVERITY = ['breach', 'watch', 'near', 'pass']

const RISK_DIRECTION_LABEL: Record<string, string> = {
  improving: 'Improving',
  stable: 'Stable',
  negative: 'Negative',
  down: 'Down',
}

const OUTLOOK_LABEL: Record<string, string> = {
  refinance_likely: 'Refinance likely',
  extension: 'Extension',
  paydown_needed: 'Paydown needed',
  at_risk: 'At risk',
}

// ── Formatting ──────────────────────────────────────────────────────────────

function fig(
  label: string,
  value: number | string | null,
  format: FigureFormat,
  extra?: Partial<Figure>,
): Figure {
  return { label, value, format, ...extra }
}

/** '3d late' / '12d' — the unit an asset manager reads deadlines in. */
function fmtDays(value: number | null | undefined, suffix = 'd'): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return NO_VALUE
  return `${Math.round(value)}${suffix}`
}

/** The server scores and orders the attention list; this only maps its
 *  severity vocabulary onto the shared Figure tone so the rank badge, the
 *  exposure figure and the row accent all read the same judgement. */
const ATTENTION_TONE: Record<string, FigureTone> = {
  high: 'negative',
  medium: 'warning',
  low: 'neutral',
}

/** 'Overdue' / 'Today' / '12d' — a deadline as an asset manager reads it. */
function attentionDue(days: number | null): string | undefined {
  if (days === null || !Number.isFinite(days)) return undefined
  if (days < 0) return 'Overdue'
  if (days < 1) return 'Today'
  return `${Math.round(days)}d`
}

function titleCase(value: string | null | undefined): string {
  if (!value) return NO_VALUE
  return value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ')
}

/**
 * Internal scale is 1 (best) to 8 (worst); 5 and worse is criticized, matching
 * `ASSET_CRITICIZED_RATING` in the service. Tone bands follow that boundary so
 * the colour on the page and the figure on the KPI strip agree.
 */
function riskDotClass(rating: number | null): string {
  if (rating === null || !Number.isFinite(rating)) return 'bg-stone-700'
  if (rating >= 7) return 'bg-red-400'
  if (rating >= 5) return 'bg-amber-300'
  if (rating >= 3) return 'bg-stone-400'
  return 'bg-emerald-400'
}

function DirectionMark({ direction }: { direction: string | null }) {
  if (!direction) return <span className="text-stone-600">{NO_VALUE}</span>
  const isUp = direction === 'improving'
  const isFlat = direction === 'stable'
  const Icon = isUp ? ArrowUp : isFlat ? Minus : ArrowDown
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-0.5 text-[10px]',
        isUp ? 'text-emerald-400' : isFlat ? 'text-stone-500' : 'text-red-400',
      )}
    >
      <Icon size={9} />
      {RISK_DIRECTION_LABEL[direction] ?? titleCase(direction)}
    </span>
  )
}

function TonePill({ label, tone }: { label: string; tone?: 'positive' | 'warning' | 'negative' }) {
  return (
    <span
      className={clsx(
        'inline-flex rounded border px-1.5 py-px font-mono text-[9px] uppercase tracking-widest',
        tone === 'positive'
          ? 'border-emerald-400/25 bg-emerald-400/[0.06] text-emerald-300'
          : tone === 'warning'
            ? 'border-amber-400/25 bg-amber-400/[0.06] text-amber-300'
            : tone === 'negative'
              ? 'border-red-400/25 bg-red-400/[0.06] text-red-300'
              : 'border-amber-400/12 bg-white/[0.02] text-stone-400',
      )}
    >
      {label}
    </span>
  )
}

// ── Toolbar primitives ──────────────────────────────────────────────────────

function ToolbarButton({
  active,
  onClick,
  disabled,
  title,
  children,
}: {
  active?: boolean
  onClick?: () => void
  disabled?: boolean
  title?: string
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={clsx(
        'flex items-center gap-1 rounded border px-2 py-1 text-[10px] transition-colors',
        active
          ? 'border-amber-400/40 bg-amber-400/10 text-amber-200'
          : 'border-amber-400/15 text-stone-400 hover:border-amber-400/30 hover:text-amber-200',
        disabled && 'cursor-not-allowed opacity-40 hover:border-amber-400/15 hover:text-stone-400',
      )}
    >
      {children}
    </button>
  )
}

/** Page-level sub-navigation. Distinct from PORTFOLIO_TABS, which filter table
 *  rows inside the surveillance panel. */
const TABS: DashboardTab[] = [
  { id: 'portfolio', label: 'Portfolio' },
  { id: 'risk', label: 'Risk & Actions' },
  { id: 'maturity', label: 'Maturity & Performance' },
]

const PORTFOLIO_TABS = [
  { key: 'all', label: 'All assets' },
  { key: 'action', label: 'Needs action' },
  { key: 'reporting', label: 'Reporting due' },
  { key: 'watchlist', label: 'Watchlist' },
  { key: 'maturing', label: 'Maturing 12 months' },
] as const

type PortfolioTab = (typeof PORTFOLIO_TABS)[number]['key']

const INSPECTOR_TABS = ['Overview', 'Performance', 'Covenants', 'Activity', 'Audit'] as const
type InspectorTab = (typeof INSPECTOR_TABS)[number]

function PillTabs<T extends string>({
  tabs,
  active,
  onSelect,
}: {
  tabs: readonly { key: T; label: string }[]
  active: T
  onSelect: (key: T) => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-1">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          onClick={() => onSelect(tab.key)}
          className={clsx(
            'rounded-full border px-2.5 py-0.5 text-[10px] transition-colors',
            active === tab.key
              ? 'border-amber-400/40 bg-amber-400/10 text-amber-200'
              : 'border-amber-400/12 text-stone-500 hover:border-amber-400/25 hover:text-stone-300',
          )}
        >
          {tab.label}
        </button>
      ))}
    </div>
  )
}

// ── The board query ─────────────────────────────────────────────────────────

/**
 * Local twin of the kit's `useDashboard`, which has no scope parameter. The
 * asset board is a personal queue and its endpoint accepts `?manager=`, so the
 * portfolio picker has to reach the request; the query key carries the scope
 * so switching managers cannot serve another manager's cached document.
 */
function useAssetBoard(manager: string | null) {
  const query = useQuery<DashboardPayload>({
    queryKey: ['dashboards', 'asset', manager ?? 'me'],
    queryFn: ({ signal }) =>
      api
        .get<DashboardPayload>('/dashboards/asset', {
          params: manager ? { manager } : undefined,
          signal,
        })
        .then((response) => response.data),
    refetchInterval: DASHBOARD_REFETCH_MS,
    refetchOnWindowFocus: true,
  })

  return {
    panels: (query.data?.panels ?? {}) as unknown as Record<string, unknown>,
    partition: query.data?.partition ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    dataUpdatedAt: query.dataUpdatedAt,
  }
}

// ── Surveillance table ──────────────────────────────────────────────────────

interface SurveillanceRow extends PortfolioEntry {
  /** Worst flagged covenant result the board shipped for this loan, if any. */
  covenantResult: string | null
  reportingDaysLate: number | null
  lastUpdateAt: string | null
}

interface SurveillanceColumn {
  key: string
  label: string
  align?: 'left' | 'right' | 'center'
  width?: string
  /** Columns the operator may hide from the Columns menu. */
  hideable?: boolean
  sortValue: (row: SurveillanceRow) => string | number
  render: (row: SurveillanceRow) => ReactNode
}

const MISSING_SORT = Number.NEGATIVE_INFINITY

function SurveillanceTable({
  rows,
  columns,
  density,
  selectedId,
  onSelect,
}: {
  rows: SurveillanceRow[]
  columns: SurveillanceColumn[]
  density: 'compact' | 'comfortable'
  selectedId: string | null
  onSelect: (row: SurveillanceRow) => void
}) {
  const navigate = useNavigate()
  const { sortColumn, sortDir, toggleSort, sortedRows } = useGenericTableSort<SurveillanceRow>(
    rows,
    (row, index) => columns[index]?.sortValue(row) ?? MISSING_SORT,
    null,
  )

  return (
    <div className="min-h-0 overflow-x-auto">
      <table className={clsx('dense-table', density === 'compact' && '[&_td]:!py-0.5')}>
        <thead>
          <tr className={dealTableHeadRowClass()}>
            {columns.map((column, index) => (
              <GenericSortHeader
                key={column.key}
                label={column.label}
                columnIndex={index}
                sortColumn={sortColumn}
                sortDir={sortDir}
                onSort={toggleSort}
                align={column.align}
                className={column.width}
              />
            ))}
            <th className="w-[36px]" aria-label="Open in workspace" />
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row) => (
            <tr
              key={row.id}
              onClick={() => onSelect(row)}
              className={dealTableRowClass(
                row.id === selectedId,
                'cursor-pointer whitespace-nowrap',
              )}
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={clsx(
                    column.align === 'right'
                      ? 'text-right'
                      : column.align === 'center'
                        ? 'text-center'
                        : 'text-left',
                  )}
                >
                  {column.render(row)}
                </td>
              ))}
              <td className="text-right">
                {row.href && (
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation()
                      navigate(row.href as string)
                    }}
                    title="Open in workspace"
                    aria-label="Open in workspace"
                    className="rounded p-0.5 text-stone-600 transition-colors hover:text-amber-300"
                  >
                    <Layers size={11} />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Inspector ───────────────────────────────────────────────────────────────

function InspectorRow({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <div className="flex items-baseline justify-between gap-2 border-b border-amber-400/[0.06] py-1 last:border-b-0">
      <span className="font-mono text-[9px] uppercase tracking-widest text-stone-500">{label}</span>
      <span className="min-w-0 truncate text-right text-[11px] text-stone-200">{children}</span>
    </div>
  )
}

function InspectorSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-1">
      <h3 className="font-mono text-[9px] uppercase tracking-widest text-amber-300/70">{title}</h3>
      {children}
    </section>
  )
}

function InspectorEmpty({ children }: { children: ReactNode }) {
  return (
    <p className="rounded border border-amber-400/[0.08] bg-white/[0.015] px-2 py-2 text-[10.5px] leading-relaxed text-stone-500">
      {children}
    </p>
  )
}

const SEVERITY_DOT: Record<string, string> = {
  critical: 'bg-red-400',
  high: 'bg-amber-300',
  medium: 'bg-amber-400/60',
  low: 'bg-stone-500',
}

interface OpenItem {
  id: string
  severity: keyof typeof SEVERITY_DOT
  title: string
  detail: string
  drill?: DrillDescriptor
}

function AssetInspector({
  row,
  covenants,
  requests,
  onClose,
}: {
  row: SurveillanceRow
  covenants: CovenantEntry[]
  requests: RequestEntry[]
  onClose: () => void
}) {
  const navigate = useNavigate()
  const { open } = useDrillDown()
  const [tab, setTab] = useState<InspectorTab>('Overview')

  const openItems = useMemo<OpenItem[]>(() => {
    const items: OpenItem[] = []
    for (const test of covenants) {
      if (test.result === 'pass') continue
      items.push({
        id: `cov-${test.id}`,
        severity: test.result === 'breach' ? 'critical' : test.result === 'watch' ? 'high' : 'medium',
        title: `Covenant ${test.result ?? 'test'}`,
        detail: test.cureDeadline ? `Cure by ${fmtDate(test.cureDeadline)}` : fmtDate(test.testDate),
        drill: test.drill,
      })
    }
    for (const request of requests) {
      const days = request.daysToDue
      items.push({
        id: `req-${request.id}`,
        severity: days !== null && days < 0 ? 'high' : days !== null && days <= 7 ? 'medium' : 'low',
        title: titleCase(request.requestType),
        detail: request.dueAt ? `Due ${fmtDate(request.dueAt)}` : 'No due date recorded',
        drill: request.drill,
      })
    }
    if (row.reportingStatus === 'late') {
      items.push({
        id: 'reporting-late',
        severity: 'high',
        title: 'Borrower reporting late',
        detail:
          row.reportingDaysLate === null
            ? 'Days late not recorded'
            : `${fmtDays(row.reportingDaysLate)} past due`,
        drill: row.drill,
      })
    }
    return items
  }, [covenants, requests, row])

  const activity = useMemo(() => {
    const entries: { id: string; at: string | null; label: string }[] = [
      { id: 'update', at: row.lastUpdateAt, label: 'Last asset-manager update' },
      ...covenants.map((test) => ({
        id: `cov-${test.id}`,
        at: test.testDate,
        label: `Covenant test — ${test.result ?? NO_VALUE}`,
      })),
      ...requests.map((request) => ({
        id: `req-${request.id}`,
        at: request.submittedAt,
        label: `${titleCase(request.requestType)} submitted`,
      })),
    ].filter((entry) => entry.at)
    return entries.sort((a, b) => String(b.at).localeCompare(String(a.at)))
  }, [covenants, requests, row.lastUpdateAt])

  return (
    <div className="flex h-full min-h-0 flex-col gap-2.5 overflow-y-auto scrollbar-thin pl-2.5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="terminal-kicker !text-[9px]">Asset inspector</div>
          <h2 className="truncate text-[13px] font-semibold text-stone-100" title={row.deal ?? ''}>
            {row.deal ?? row.loanId ?? NO_VALUE}
          </h2>
          <p className="truncate font-mono text-[9.5px] text-stone-500">{row.loanId ?? NO_VALUE}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close inspector"
          className="rounded p-0.5 text-stone-600 transition-colors hover:text-amber-300"
        >
          <X size={13} />
        </button>
      </div>

      <div className="flex flex-wrap gap-1">
        {INSPECTOR_TABS.map((name) => (
          <button
            key={name}
            type="button"
            onClick={() => setTab(name)}
            className={clsx(
              'rounded border px-2 py-0.5 text-[10px] transition-colors',
              tab === name
                ? 'border-amber-400/40 bg-amber-400/10 text-amber-200'
                : 'border-amber-400/12 text-stone-500 hover:text-stone-300',
            )}
          >
            {name}
          </button>
        ))}
      </div>

      {tab === 'Overview' && (
        <>
          <InspectorSection title="Decision snapshot">
            <div className="surface-panel rounded-md px-2.5 py-2">
              <div className="flex items-center justify-between gap-2">
                <TonePill
                  label={titleCase(row.tier)}
                  tone={row.tier ? TIER_TONE[row.tier] : undefined}
                />
                <span className="font-mono text-[10px] text-stone-400">
                  Risk {row.riskRating ?? NO_VALUE}
                </span>
              </div>
              <p className="mt-1.5 text-[10.5px] leading-relaxed text-stone-400">
                {row.nextMilestoneAt
                  ? `Next review ${fmtDate(row.nextMilestoneAt)}${
                      row.daysToMilestone === null ? '' : ` (${fmtDays(row.daysToMilestone)})`
                    }.`
                  : 'No review date recorded for this asset.'}
              </p>
            </div>
          </InspectorSection>

          <div className="grid grid-cols-2 gap-1.5">
            <div className="metric-cell">
              <div className="metric-cell-label truncate">Property</div>
              <div className="truncate text-[11.5px] text-stone-200">{row.deal ?? NO_VALUE}</div>
            </div>
            <div className="metric-cell">
              <div className="metric-cell-label truncate">Sponsor</div>
              <div className="truncate text-[11.5px] text-stone-600">{NO_VALUE}</div>
            </div>
            <div className="metric-cell">
              <div className="metric-cell-label truncate">Risk</div>
              <div className="flex items-center gap-1.5 text-[11.5px] text-stone-200">
                <span className={clsx('status-dot', riskDotClass(row.riskRating))} />
                {row.riskRating ?? NO_VALUE}
                <DirectionMark direction={row.riskDirection} />
              </div>
            </div>
            <div className="metric-cell">
              <div className="metric-cell-label truncate">Balance</div>
              <div className="truncate text-[11.5px] text-stone-200">
                <FigureValue figure={fig('Balance', row.amount, 'currency')} />
              </div>
            </div>
          </div>

          <InspectorSection title="Original → current">
            <InspectorEmpty>
              This board does not carry at-close versus current value, LTV, NOI, DSCR or occupancy.
              Nothing is estimated here — the comparison appears once the endpoint returns it.
            </InspectorEmpty>
          </InspectorSection>

          <InspectorSection title="Covenants / reporting">
            <div className="surface-panel rounded-md px-2.5 py-1">
              <InspectorRow label="Covenants">
                {row.covenantResult ? (
                  <TonePill
                    label={row.covenantResult.toUpperCase()}
                    tone={COVENANT_TONE[row.covenantResult]}
                  />
                ) : (
                  <span className="text-stone-600">{NO_VALUE}</span>
                )}
              </InspectorRow>
              <InspectorRow label="Reporting">
                {row.reportingStatus === 'late' ? (
                  <TonePill label={`${fmtDays(row.reportingDaysLate)} late`} tone="negative" />
                ) : row.reportingStatus ? (
                  <TonePill label="Current" tone="positive" />
                ) : (
                  <span className="text-stone-600">{NO_VALUE}</span>
                )}
              </InspectorRow>
              <InspectorRow label="Maturity">{fmtDate(row.maturityOn)}</InspectorRow>
              <InspectorRow label="Exit outlook">
                {row.refinanceOutlook
                  ? (OUTLOOK_LABEL[row.refinanceOutlook] ?? titleCase(row.refinanceOutlook))
                  : NO_VALUE}
              </InspectorRow>
            </div>
          </InspectorSection>

          <InspectorSection title="Open items">
            {openItems.length === 0 ? (
              <InspectorEmpty>No open covenant, request or reporting items on this asset.</InspectorEmpty>
            ) : (
              <ul className="space-y-1">
                {openItems.map((item) => (
                  <li
                    key={item.id}
                    className="surface-panel flex items-center gap-2 rounded px-2 py-1.5"
                  >
                    <span className={clsx('status-dot flex-shrink-0', SEVERITY_DOT[item.severity])} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[11px] text-stone-200">{item.title}</span>
                      <span className="block text-[9.5px] leading-snug text-stone-500">{item.detail}</span>
                    </span>
                    {item.drill && (
                      <button
                        type="button"
                        onClick={() => open(item.drill)}
                        title={`${item.drill.title} — open records`}
                        className="flex-shrink-0 rounded border border-amber-400/20 px-1.5 py-0.5 text-[9px] text-amber-200 transition-colors hover:bg-amber-400/10"
                      >
                        Open
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </InspectorSection>

          <InspectorSection title="My next action">
            <p className="rounded border border-amber-400/[0.08] bg-white/[0.015] px-2 py-2 text-[11px] leading-relaxed text-stone-300">
              {row.nextAction ?? 'No next action recorded for this asset.'}
            </p>
          </InspectorSection>
        </>
      )}

      {tab === 'Performance' && (
        <InspectorEmpty>
          Operating performance history is not part of the asset board payload. No NOI, occupancy or
          DSCR series is inferred here.
        </InspectorEmpty>
      )}

      {tab === 'Covenants' && (
        <div className="space-y-1">
          {covenants.length === 0 ? (
            <InspectorEmpty>No flagged covenant tests shipped for this asset.</InspectorEmpty>
          ) : (
            covenants.map((test) => (
              <div key={test.id} className="surface-panel rounded-md px-2.5 py-1.5">
                <div className="flex items-center justify-between gap-2">
                  <TonePill
                    label={(test.result ?? NO_VALUE).toUpperCase()}
                    tone={test.result ? COVENANT_TONE[test.result] : undefined}
                  />
                  <span className="font-mono text-[9.5px] text-stone-500">
                    {fmtDate(test.testDate)}
                  </span>
                </div>
                <div className="mt-1 flex items-center justify-between gap-2 text-[10.5px] text-stone-400">
                  <span>
                    Actual <FigureValue figure={fig('Actual', test.actualValue, 'ratio')} /> vs{' '}
                    <FigureValue figure={fig('Threshold', test.threshold, 'ratio')} />
                  </span>
                  <span>Cure {fmtDate(test.cureDeadline)}</span>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {tab === 'Activity' && (
        <div className="space-y-1">
          {activity.length === 0 ? (
            <InspectorEmpty>No dated activity for this asset in the board payload.</InspectorEmpty>
          ) : (
            activity.map((entry) => (
              <div
                key={entry.id}
                className="flex items-baseline justify-between gap-2 border-b border-amber-400/[0.06] py-1 last:border-b-0"
              >
                <span className="min-w-0 truncate text-[11px] text-stone-300">{entry.label}</span>
                <span className="flex-shrink-0 font-mono text-[9.5px] text-stone-500">
                  {fmtDate(entry.at)}
                </span>
              </div>
            ))
          )}
        </div>
      )}

      {tab === 'Audit' && (
        <InspectorEmpty>
          The asset board does not ship an audit trail. Record-level history stays in the deal
          workspace rather than being reconstructed here.
        </InspectorEmpty>
      )}

      <div className="mt-auto space-y-1.5 border-t border-amber-400/[0.08] pt-2">
        <div className="grid grid-cols-4 gap-1">
          {[
            { label: 'Call', Icon: Phone },
            { label: 'Email', Icon: Mail },
            { label: 'Request', Icon: ClipboardList },
            { label: 'Add note', Icon: StickyNote },
          ].map(({ label, Icon }) => (
            <button
              key={label}
              type="button"
              disabled
              title="No borrower contact channel is wired to this board"
              className="flex cursor-not-allowed flex-col items-center gap-0.5 rounded border border-amber-400/10 px-1 py-1.5 text-[9px] text-stone-600"
            >
              <Icon size={11} />
              {label}
            </button>
          ))}
        </div>
        <div className="flex gap-1.5">
          <button
            type="button"
            disabled={!row.href}
            onClick={() => row.href && navigate(row.href)}
            className="flex flex-1 items-center justify-center gap-1 rounded border border-amber-400/25 px-2 py-1 text-[10px] text-amber-200 transition-colors hover:bg-amber-400/10 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
          >
            <ExternalLink size={10} /> Open asset
          </button>
          <button
            type="button"
            disabled
            title="Risk escalation is not an action this board can take"
            className="flex flex-1 cursor-not-allowed items-center justify-center gap-1 rounded border border-amber-400/10 px-2 py-1 text-[10px] text-stone-600"
          >
            <ShieldAlert size={10} /> Escalate risk
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Page ────────────────────────────────────────────────────────────────────

export default function AssetManagerDashboard() {
  const navigate = useNavigate()
  const { open } = useDrillDown()

  // The book opens whole: a CEO or desk head with one or two monitored assets
  // of their own would otherwise read a two-loan book. "My portfolio" is one
  // pick away; the choice lives in the URL so a link opens the same scope.
  const [params, setParams] = useSearchParams()
  const pickedManager = params.get('manager') ?? DESK_SCOPE
  const manager = pickedManager === 'me' ? null : pickedManager
  const setManager = (value: string | null) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set('manager', value ?? 'me')
        return next
      },
      { replace: true },
    )
  const board = useAssetBoard(manager)
  const { panels, isLoading, error } = board
  const retry = () => void board.refetch()

  const [searchDraft, setSearchDraft] = useState('')
  const [search, setSearch] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [showColumns, setShowColumns] = useState(false)
  const [tierFilter, setTierFilter] = useState<string | null>(null)
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([])
  const [density, setDensity] = useState<'compact' | 'comfortable'>('compact')
  const [tab, setTab] = useState<PortfolioTab>('all')
  const [active, setActive] = useDashboardView(TABS)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [inspectorOpen, setInspectorOpen] = useState(false)

  const scope = readPanel<ScopePanel>(panels, 'scope')
  const universe = readPanel<BookUniverse>(panels, 'universe')
  const headline = readPanel<TilesPanelData>(panels, 'headline')
  const tiers = readPanel<{ rows: TierEntry[] }>(panels, 'tiers')
  const portfolio = readPanel<{ rows: PortfolioEntry[] }>(panels, 'portfolio')
  const covenants = readPanel<CovenantPanel>(panels, 'covenants')
  const requests = readPanel<RequestsPanel>(panels, 'requests')
  const maturities = readPanel<MaturityPanel>(panels, 'maturities')
  const reporting = readPanel<ReportingPanel>(panels, 'reporting')
  const performance = readPanel<PerformanceMovementPanel>(panels, 'performanceMovement')
  const attention = readPanel<AttentionPanel>(panels, 'attention')
  const roster = readPanel<{ rows: RosterEntry[] }>(panels, 'roster')

  /** The ranked action list, shaped for the shared AttentionTable.
   *
   *  Rank is the server's array order (asset.attention_panel scores elevated
   *  tiers, covenant breaches and pending borrower requests together and calls
   *  `rank_attention`) — this never re-sorts, so the order on screen is the
   *  order the inspectable scoring rule produced. */
  const attentionData = useMemo<AttentionPanelData>(
    () => ({
      items: (attention?.rows ?? []).map((row, index) => ({
        id: `${row.source}-${index}`,
        rank: index + 1,
        title: row.title,
        subtitle: [row.severity ? `${titleCase(row.severity)} risk` : null, row.detail]
          .filter(Boolean)
          .join(' · '),
        figure: fig('Exposure', row.amount, 'currency', {
          drill: row.drill ?? undefined,
          tone: row.severity ? ATTENTION_TONE[row.severity] : undefined,
        }),
        dueLabel: attentionDue(row.daysToDeadline),
        action: { label: row.action, drill: row.drill ?? undefined, href: row.href ?? undefined },
        tone: row.severity ? ATTENTION_TONE[row.severity] : undefined,
      })),
      footnote: 'Ranked server-side by exposure, urgency and severity.',
    }),
    [attention],
  )

  /** Covenant status and reporting lateness live in sibling panels; the
   *  surveillance row joins them on loan id rather than re-deriving either. */
  const rows = useMemo<SurveillanceRow[]>(() => {
    const worstCovenant = new Map<string, string>()
    for (const test of covenants?.rows ?? []) {
      if (!test.loanId || !test.result) continue
      const current = worstCovenant.get(test.loanId)
      const rank = COVENANT_SEVERITY.indexOf(test.result)
      if (rank < 0) continue
      if (current === undefined || rank < COVENANT_SEVERITY.indexOf(current)) {
        worstCovenant.set(test.loanId, test.result)
      }
    }
    const lateness = new Map<string, ReportingEntry>()
    for (const entry of reporting?.rows ?? []) {
      if (entry.loanId) lateness.set(entry.loanId, entry)
    }
    return (portfolio?.rows ?? []).map((entry) => {
      const late = entry.loanId ? lateness.get(entry.loanId) : undefined
      return {
        ...entry,
        covenantResult: entry.loanId ? (worstCovenant.get(entry.loanId) ?? null) : null,
        reportingDaysLate: late?.daysLate ?? null,
        lastUpdateAt: late?.lastUpdateAt ?? null,
      }
    })
  }, [portfolio, covenants, reporting])

  const horizon = useMemo(() => {
    const date = new Date()
    date.setFullYear(date.getFullYear() + 1)
    return date.toISOString().slice(0, 10)
  }, [])

  const visibleRows = useMemo(() => {
    const needle = search.trim().toLowerCase()
    return rows.filter((row) => {
      if (tierFilter && row.tier !== tierFilter) return false
      if (tab === 'action') {
        const elevated = row.tier ? ELEVATED_TIERS.has(row.tier) : false
        if (!elevated && row.reportingStatus !== 'late' && row.covenantResult !== 'breach') {
          return false
        }
      }
      if (tab === 'reporting' && row.reportingStatus !== 'late') return false
      if (tab === 'watchlist' && row.tier !== 'watchlist') return false
      if (tab === 'maturing' && (!row.maturityOn || row.maturityOn.slice(0, 10) > horizon)) {
        return false
      }
      if (!needle) return true
      return [row.deal, row.loanId, row.tier, row.nextAction, row.refinanceOutlook]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(needle))
    })
  }, [rows, search, tab, tierFilter, horizon])

  const allColumns = useMemo<SurveillanceColumn[]>(
    () => [
      {
        key: 'deal',
        label: 'Loan / property',
        width: 'min-w-[190px]',
        sortValue: (row) => row.deal ?? row.loanId ?? '',
        render: (row) => (
          <FigureValue
            figure={fig('Loan / property', row.deal ?? row.loanId, 'text', { drill: row.drill })}
          />
        ),
      },
      {
        key: 'risk',
        label: 'Risk',
        width: 'w-[130px]',
        sortValue: (row) => row.riskRating ?? MISSING_SORT,
        render: (row) => (
          <span className="inline-flex items-center gap-1.5">
            <span className={clsx('status-dot', riskDotClass(row.riskRating))} />
            <span className="font-mono text-[11px] text-stone-200">
              {row.riskRating ?? NO_VALUE}
            </span>
            <DirectionMark direction={row.riskDirection} />
          </span>
        ),
      },
      {
        key: 'tier',
        label: 'Tier',
        width: 'w-[110px]',
        hideable: true,
        sortValue: (row) => row.tier ?? '',
        render: (row) => (
          <TonePill label={titleCase(row.tier)} tone={row.tier ? TIER_TONE[row.tier] : undefined} />
        ),
      },
      {
        key: 'balance',
        label: 'Balance',
        align: 'right',
        width: 'w-[110px]',
        sortValue: (row) => row.amount ?? MISSING_SORT,
        render: (row) => <FigureValue figure={fig('Balance', row.amount, 'currency')} align="right" />,
      },
      {
        key: 'covenants',
        label: 'Covenants',
        align: 'center',
        width: 'w-[90px]',
        sortValue: (row) => row.covenantResult ?? '',
        render: (row) =>
          row.covenantResult ? (
            <TonePill
              label={row.covenantResult.toUpperCase()}
              tone={COVENANT_TONE[row.covenantResult]}
            />
          ) : (
            <span className="text-stone-600">{NO_VALUE}</span>
          ),
      },
      {
        key: 'reporting',
        label: 'Reporting',
        align: 'center',
        width: 'w-[95px]',
        sortValue: (row) => row.reportingDaysLate ?? (row.reportingStatus === 'current' ? 0 : MISSING_SORT),
        render: (row) =>
          row.reportingStatus === 'late' ? (
            <TonePill label={`${fmtDays(row.reportingDaysLate)} late`} tone="negative" />
          ) : row.reportingStatus ? (
            <TonePill label="Current" tone="positive" />
          ) : (
            <span className="text-stone-600">{NO_VALUE}</span>
          ),
      },
      {
        key: 'maturity',
        label: 'Maturity',
        align: 'right',
        width: 'w-[95px]',
        sortValue: (row) => row.maturityOn ?? '',
        render: (row) => <span className="font-mono text-[10.5px]">{fmtDate(row.maturityOn)}</span>,
      },
      {
        key: 'outlook',
        label: 'Exit outlook',
        width: 'w-[125px]',
        hideable: true,
        sortValue: (row) => row.refinanceOutlook ?? '',
        render: (row) => (
          <span className="text-[11px] text-stone-300">
            {row.refinanceOutlook
              ? (OUTLOOK_LABEL[row.refinanceOutlook] ?? titleCase(row.refinanceOutlook))
              : NO_VALUE}
          </span>
        ),
      },
      {
        key: 'nextAction',
        label: 'Next action',
        width: 'min-w-[170px]',
        hideable: true,
        sortValue: (row) => row.nextAction ?? '',
        render: (row) => (
          <span className="block max-w-[240px] truncate text-[11px] text-stone-400">
            {row.nextAction ?? NO_VALUE}
          </span>
        ),
      },
      {
        key: 'milestone',
        label: 'Next review',
        align: 'right',
        width: 'w-[95px]',
        hideable: true,
        sortValue: (row) => row.daysToMilestone ?? MISSING_SORT,
        render: (row) => (
          <span className="font-mono text-[10.5px]">{fmtDate(row.nextMilestoneAt)}</span>
        ),
      },
    ],
    [],
  )

  const columns = useMemo(
    () => allColumns.filter((column) => !hiddenColumns.includes(column.key)),
    [allColumns, hiddenColumns],
  )

  const selected = useMemo(
    () => rows.find((row) => row.id === selectedId) ?? null,
    [rows, selectedId],
  )

  const selectedCovenants = useMemo(
    () => (covenants?.rows ?? []).filter((test) => test.loanId && test.loanId === selected?.loanId),
    [covenants, selected],
  )
  const selectedRequests = useMemo(
    () => (requests?.rows ?? []).filter((item) => item.loanId && item.loanId === selected?.loanId),
    [requests, selected],
  )

  const watchlistPlans = useMemo(
    () => rows.filter((row) => (row.tier ? ELEVATED_TIERS.has(row.tier) : false)),
    [rows],
  )

  const maturityMax = useMemo(() => {
    const values = (maturities?.buckets ?? [])
      .map((bucket) => (typeof bucket.balance.value === 'number' ? bucket.balance.value : 0))
      .filter((value) => Number.isFinite(value))
    return values.length ? Math.max(...values) : 0
  }, [maturities])

  const atRisk = (maturities?.outlook ?? []).find((item) => item.label === OUTLOOK_LABEL.at_risk)

  const toolbar = (
    <>
      <select
        value={manager ?? ''}
        onChange={(event) => {
          setManager(event.target.value || null)
          setSelectedId(null)
        }}
        aria-label="Portfolio"
        className="input input-compact max-w-[210px]"
      >
        <option value={DESK_SCOPE}>Whole book</option>
        <option value="">My portfolio</option>
        {(roster?.rows ?? []).map((entry) => (
          <option
            key={entry.assetManagerEmail ?? 'unassigned'}
            value={entry.scopeValue ?? entry.assetManagerEmail ?? ''}
          >
            {entry.assetManagerEmail ?? 'Unassigned'} · {entry.assets}
          </option>
        ))}
      </select>
      <ToolbarButton onClick={() => navigate('/reports')} title="Open the report studio">
        <FileText size={10} /> Report studio
      </ToolbarButton>
      <ToolbarButton onClick={() => window.print()} title="Print this board">
        <Printer size={10} /> Print
      </ToolbarButton>
      <ToolbarButton
        active={inspectorOpen}
        onClick={() => setInspectorOpen((value) => !value)}
        title={inspectorOpen ? 'Hide the asset inspector' : 'Show the asset inspector'}
      >
        {inspectorOpen ? <PanelRightClose size={10} /> : <PanelRightOpen size={10} />} Inspector
      </ToolbarButton>
    </>
  )

  // ── Monitoring tiers band (Portfolio tab, row 1) ──────────────────────────
  const tiersBand = (
    <div className="flex min-h-0 flex-1 flex-col">
      {isLoading ? (
        <div className="grid h-full grid-cols-3 gap-2 xl:grid-cols-6">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="metric-cell" />
          ))}
        </div>
      ) : (tiers?.rows.length ?? 0) === 0 ? (
        <div className="surface-panel rounded-md px-3 py-2 text-[11px] text-stone-500">
          No monitoring tiers in this partition yet.
        </div>
      ) : (
        <div className="grid h-full grid-cols-3 gap-2 xl:grid-cols-6">
          {tiers?.rows.map((entry) => {
            const tone = TIER_TONE[entry.tier]
            return (
              <div
                key={entry.tier}
                className={clsx(
                  'metric-cell flex min-w-0 flex-col',
                  tone === 'negative'
                    ? '!border-red-400/25'
                    : tone === 'warning'
                      ? '!border-amber-400/25'
                      : undefined,
                )}
              >
                <div className="metric-cell-label truncate">{entry.label}</div>
                <div
                  className={clsx('metric-cell-value truncate !text-[15px]', figureToneClass(tone))}
                >
                  <FigureValue figure={entry.count} />
                </div>
                <div className="truncate font-mono text-[10px] text-stone-500">
                  <FigureValue figure={entry.balance} />
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )

  // ── Query / filter toolbar (Portfolio tab only) ───────────────────────────
  const queryToolbar = (
    <div className="flex flex-shrink-0 flex-col gap-2">
      <div className="surface-panel flex flex-wrap items-center gap-1.5 rounded-md px-2 py-1.5">
        <div className="flex min-w-[220px] flex-1 items-center gap-1.5">
          <Search size={12} className="flex-shrink-0 text-stone-600" />
          <input
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') setSearch(searchDraft)
            }}
            placeholder="Filter my portfolio — property, deal id, tier, next action"
            aria-label="Filter my portfolio"
            className="input input-compact w-full"
          />
        </div>
        <span className="hidden text-[10px] text-stone-600 lg:inline">
          Matches property, deal id, tier, exit outlook and next action
        </span>
        <ToolbarButton onClick={() => setSearch(searchDraft)}>Apply</ToolbarButton>
        <ToolbarButton active={showFilters} onClick={() => setShowFilters((v) => !v)}>
          <SlidersHorizontal size={10} /> Filters
        </ToolbarButton>
        <ToolbarButton active={showColumns} onClick={() => setShowColumns((v) => !v)}>
          <Columns3 size={10} /> Columns
        </ToolbarButton>
        <div className="flex items-center gap-1 rounded border border-amber-400/15 p-0.5">
          {(['compact', 'comfortable'] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setDensity(value)}
              className={clsx(
                'rounded px-1.5 py-0.5 text-[9px] uppercase tracking-widest transition-colors',
                density === value ? 'bg-amber-400/10 text-amber-200' : 'text-stone-500',
              )}
            >
              {value === 'compact' ? 'Dense' : 'Roomy'}
            </button>
          ))}
        </div>
      </div>

      {showFilters && (
        <div className="surface-panel flex flex-wrap items-center gap-1.5 rounded-md px-2 py-1.5">
          <span className="font-mono text-[9px] uppercase tracking-widest text-stone-500">Tier</span>
          <ToolbarButton active={tierFilter === null} onClick={() => setTierFilter(null)}>
            Any
          </ToolbarButton>
          {tiers?.rows.map((entry) => (
            <ToolbarButton
              key={entry.tier}
              active={tierFilter === entry.tier}
              onClick={() => setTierFilter(tierFilter === entry.tier ? null : entry.tier)}
            >
              {entry.label}
            </ToolbarButton>
          ))}
        </div>
      )}

      {showColumns && (
        <div className="surface-panel flex flex-wrap items-center gap-1.5 rounded-md px-2 py-1.5">
          <span className="font-mono text-[9px] uppercase tracking-widest text-stone-500">
            Columns
          </span>
          {allColumns
            .filter((column) => column.hideable)
            .map((column) => (
              <ToolbarButton
                key={column.key}
                active={!hiddenColumns.includes(column.key)}
                onClick={() =>
                  setHiddenColumns((current) =>
                    current.includes(column.key)
                      ? current.filter((key) => key !== column.key)
                      : [...current, column.key],
                  )
                }
              >
                {column.label}
              </ToolbarButton>
            ))}
        </div>
      )}
    </div>
  )

  // ── Panels ────────────────────────────────────────────────────────────────
  const surveillancePanel = (
    <Panel
      title="My portfolio surveillance"
      kicker={`${visibleRows.length} of ${rows.length}`}
      actions={<PillTabs tabs={PORTFOLIO_TABS} active={tab} onSelect={setTab} />}
      isLoading={isLoading}
      isEmpty={!isLoading && !error && visibleRows.length === 0}
      emptyIcon={Building2}
      emptyTitle={rows.length === 0 ? 'No monitored assets' : 'No assets match this view'}
      emptyDescription={
        rows.length === 0
          ? 'This portfolio has no monitored assets in the active partition. Figures appear once assets are onboarded to surveillance.'
          : 'Clear the search, tier filter or tab to widen the view.'
      }
      scroll
      bodyClassName="!p-0"
      footer={
        <span className="flex items-center gap-1.5">
          <AlertTriangle size={10} className="text-amber-400/70" />
          The covenant column reflects only the flagged tests this board ships — a blank cell means no
          flagged test, not a pass.
        </span>
      }
      className="flex-1"
    >
      <SurveillanceTable
        rows={visibleRows}
        columns={columns}
        density={density}
        selectedId={selectedId}
        onSelect={(row) => {
          setSelectedId(row.id)
          setInspectorOpen(true)
        }}
      />
    </Panel>
  )

  const movementCell = (fig: Figure, colKey: string) => {
    const Icon = colKey === 'up' ? ArrowUp : colKey === 'down' ? ArrowDown : Minus
    const iconClass =
      colKey === 'up'
        ? 'text-emerald-400'
        : colKey === 'down'
          ? 'text-red-400'
          : 'text-stone-500'
    const inner = (
      <span className="flex items-center justify-end gap-1 font-mono tabular-nums">
        <Icon size={11} className={clsx('flex-shrink-0', iconClass)} />
        <FigureValue figure={fig} align="right" />
      </span>
    )
    return fig.drill ? (
      <button
        type="button"
        onClick={() => open(fig.drill)}
        title={`${fig.drill.title} — open records`}
        className="ml-auto block rounded px-1 text-right transition-colors hover:bg-white/[0.03] focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-400/50"
      >
        {inner}
      </button>
    ) : (
      inner
    )
  }

  const performancePanel = (
    <Panel
      title="Performance movement"
      kicker={performance ? 'Up / Flat / Down' : undefined}
      isLoading={isLoading}
      error={error}
      onRetry={retry}
      isEmpty={!isLoading && !error && (performance?.rows.length ?? 0) === 0}
      emptyIcon={TrendingUp}
      emptyTitle="No movement recorded"
      emptyDescription="No monitored asset in this partition carries a period-over-period direction yet."
      footer={
        performance?.deterioratingExposure ? (
          <span className="flex items-center gap-1.5">
            <TrendingUp size={11} className="rotate-180 text-red-400" />
            Exposure deteriorating{' '}
            <span className="font-mono text-red-300">
              {displayFigure(performance.deterioratingExposure)}
            </span>
          </span>
        ) : undefined
      }
      scroll
      bodyClassName="!p-0"
      className="flex-1"
    >
      {performance && performance.rows.length > 0 && (
        <table className="dense-table w-full">
          <thead>
            <tr>
              <th className="text-left">Metric</th>
              {performance.columns.map((c) => (
                <th key={c.key} className="text-right">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {performance.rows.map((row) => (
              <tr key={row.key}>
                <td className="text-left text-stone-300">{row.label}</td>
                <td className="text-right">{movementCell(row.up, 'up')}</td>
                <td className="text-right">{movementCell(row.flat, 'flat')}</td>
                <td className="text-right">{movementCell(row.down, 'down')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  )

  const covenantsPanel = (
    <Panel
      title="Covenants & reporting"
      kicker={covenants?.passRate ? <FigureValue figure={covenants.passRate} /> : undefined}
      isLoading={isLoading}
      isEmpty={
        !isLoading &&
        !error &&
        (covenants?.summary.length ?? 0) === 0 &&
        (reporting?.rows.length ?? 0) === 0
      }
      emptyIcon={Inbox}
      emptyTitle="No covenant tests"
      emptyDescription="No covenant test results or late reporting packages for this portfolio."
      scroll
      className="flex-1"
      footer="Reporting cadence and package schedules live on the deal record."
    >
      <div className="space-y-2">
        <div className="grid grid-cols-4 gap-1">
          {(covenants?.summary ?? []).map((item) => (
            <div key={item.label} className="metric-cell !px-2 !py-1.5">
              <div className="metric-cell-label truncate">{item.label}</div>
              <div className={clsx('metric-cell-value !text-[14px]', figureToneClass(item.tone))}>
                <FigureValue figure={item} />
              </div>
            </div>
          ))}
        </div>

        {(covenants?.rows.length ?? 0) > 0 && (
          <div>
            <div className="terminal-kicker !text-[9px]">Flagged tests</div>
            <table className="dense-table">
              <tbody>
                {covenants?.rows.map((test) => (
                  <tr key={test.id} className={dealTableRowClass(false, 'cursor-default')}>
                    <td className="max-w-[150px] truncate">
                      <FigureValue
                        figure={fig('Asset', test.deal ?? test.loanId, 'text', {
                          drill: test.drill,
                        })}
                      />
                    </td>
                    <td className="text-center">
                      <TonePill
                        label={(test.result ?? NO_VALUE).toUpperCase()}
                        tone={test.result ? COVENANT_TONE[test.result] : undefined}
                      />
                    </td>
                    <td className="text-right font-mono text-[10px] text-stone-500">
                      {fmtDate(test.cureDeadline)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div>
          <div className="flex items-baseline justify-between">
            <span className="terminal-kicker !text-[9px]">Reporting</span>
            {reporting?.onTimeRate && (
              <span className="font-mono text-[10px] text-stone-400">
                <FigureValue figure={reporting.onTimeRate} />
              </span>
            )}
          </div>
          {(reporting?.rows.length ?? 0) === 0 ? (
            <p className="text-[10.5px] text-stone-500">No late reporting packages.</p>
          ) : (
            <table className="dense-table">
              <tbody>
                {reporting?.rows.map((entry) => (
                  <tr key={entry.id} className={dealTableRowClass(false, 'cursor-default')}>
                    <td className="max-w-[150px] truncate">
                      <FigureValue
                        figure={fig('Asset', entry.deal ?? entry.loanId, 'text', {
                          drill: entry.drill,
                        })}
                      />
                    </td>
                    <td className="text-right">
                      <TonePill label={`${fmtDays(entry.daysLate)} late`} tone="negative" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Panel>
  )

  const maturityPanel = (
    <Panel
      title="Maturity & refinance"
      isLoading={isLoading}
      isEmpty={!isLoading && !error && (maturities?.buckets.length ?? 0) === 0}
      emptyIcon={CalendarClock}
      emptyTitle="No maturity ladder"
      emptyDescription="No monitored asset in this partition carries a parsed maturity date."
      scroll
      className="flex-1"
      footer={
        atRisk && atRisk.value !== null ? (
          <span className="flex items-center gap-1.5 text-amber-300">
            <AlertTriangle size={10} />
            <FigureValue figure={atRisk} /> assets carry an at-risk exit outlook.
          </span>
        ) : (
          'Exit outlook is recorded per asset on the monitoring record.'
        )
      }
    >
      <div className="space-y-2">
        <div className="space-y-1.5">
          {(maturities?.buckets ?? []).map((bucket, index) => {
            const balance = typeof bucket.balance.value === 'number' ? bucket.balance.value : null
            const width = balance !== null && maturityMax > 0 ? (balance / maturityMax) * 100 : 0
            const bar = (
              <span
                className="block h-2 rounded-sm"
                style={{
                  width: `${Math.max(width, balance ? 2 : 0)}%`,
                  background: chartPartOfWholeColor(index, maturities?.buckets.length ?? 1),
                }}
              />
            )
            return (
              <div key={bucket.key} className="flex items-center gap-2">
                <span className="w-[62px] flex-shrink-0 font-mono text-[10px] text-stone-400">
                  {bucket.label}
                </span>
                <span className="min-w-0 flex-1 rounded-sm" style={{ background: CHART_VIZ_TRACK }}>
                  {bucket.count.drill ? (
                    <button
                      type="button"
                      onClick={() => open(bucket.count.drill)}
                      title={`${bucket.count.drill.title} — open records`}
                      className="block w-full text-left focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-400/50"
                    >
                      {bar}
                    </button>
                  ) : (
                    bar
                  )}
                </span>
                <span className="w-[42px] flex-shrink-0 text-right font-mono text-[10px] text-stone-300">
                  <FigureValue figure={bucket.count} align="right" />
                </span>
                <span className="w-[62px] flex-shrink-0 text-right font-mono text-[10px] text-stone-500">
                  {displayFigure(bucket.balance)}
                </span>
              </div>
            )
          })}
        </div>

        <div className="grid grid-cols-2 gap-1 border-t border-amber-400/[0.06] pt-1.5">
          {(maturities?.outlook ?? []).map((item) => (
            <div key={item.label} className="flex items-center justify-between gap-1.5">
              <span className="truncate text-[10px] text-stone-500">{item.label}</span>
              <span className={clsx('font-mono text-[10.5px]', figureToneClass(item.tone))}>
                <FigureValue figure={item} align="right" />
              </span>
            </div>
          ))}
        </div>

        {maturities?.maturityNotRecorded && maturities.maturityNotRecorded.value !== null && (
          <p className="text-[10px] text-amber-300/80">
            <FigureValue figure={maturities.maturityNotRecorded} /> assets have no parsed maturity
            date and are excluded from the ladder above.
          </p>
        )}
      </div>
    </Panel>
  )

  const attentionPanel = (
    <Panel
      title="Portfolio attention"
      kicker="Ranked"
      isLoading={isLoading}
      isEmpty={!isLoading && !error && attentionData.items.length === 0}
      emptyIcon={ShieldAlert}
      emptyTitle="Nothing ranked"
      emptyDescription="Elevated tiers, covenant breaches and pending borrower requests are scored together; none clears the threshold for this portfolio."
      scroll
      className="flex-1"
    >
      <AttentionTable data={attentionData} />
    </Panel>
  )

  const requestsPanel = (
    <Panel
      title="Requests & inspections"
      isLoading={isLoading}
      isEmpty={!isLoading && !error && (requests?.byType.length ?? 0) === 0}
      emptyIcon={ClipboardList}
      emptyTitle="No borrower requests"
      emptyDescription="No pending borrower requests for the assets in this portfolio."
      scroll
      className="flex-1"
      footer="Counts are pending requests only; decided items stay on the deal record."
    >
      <table className="dense-table">
        <thead>
          <tr>
            <th className="text-left">Item</th>
            <th className="text-right">Count</th>
            <th className="text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {(requests?.byType ?? []).map((entry) => (
            <tr key={entry.requestType} className={dealTableRowClass(false, 'cursor-default')}>
              <td className="whitespace-nowrap text-stone-300">{entry.label}</td>
              <td className="text-right tabular-nums">
                <FigureValue figure={entry.count} align="right" />
              </td>
              <td className="text-right font-mono text-[10.5px] text-stone-400">
                <FigureValue figure={entry.amount} align="right" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  )

  const watchlistPanel = (
    <Panel
      title="Watchlist action plans"
      kicker={`${watchlistPlans.length} elevated`}
      isLoading={isLoading}
      isEmpty={!isLoading && !error && watchlistPlans.length === 0}
      emptyIcon={Activity}
      emptyTitle="Nothing above routine surveillance"
      emptyDescription="No asset in this portfolio sits in a heightened, watchlist, modification or workout tier."
      scroll
      bodyClassName="!p-0"
      className="flex-1"
    >
      <table className="dense-table">
        <thead>
          <tr className={dealTableHeadRowClass()}>
            <th className="text-left">Asset</th>
            <th className="text-left">Action plan</th>
            <th className="text-right">Next milestone</th>
            <th className="text-left">Risk</th>
            <th className="text-left">Owner</th>
            <th className="text-right">Last update</th>
          </tr>
        </thead>
        <tbody>
          {watchlistPlans.map((row) => (
            <tr
              key={row.id}
              onClick={() => {
                setSelectedId(row.id)
                setInspectorOpen(true)
              }}
              className={dealTableRowClass(row.id === selectedId, 'cursor-pointer')}
            >
              <td className="max-w-[190px] truncate">
                <FigureValue
                  figure={fig('Asset', row.deal ?? row.loanId, 'text', { drill: row.drill })}
                />
              </td>
              <td className="max-w-[280px] truncate text-stone-400">{row.nextAction ?? NO_VALUE}</td>
              <td className="whitespace-nowrap text-right font-mono text-[10.5px]">
                {fmtDate(row.nextMilestoneAt)}
              </td>
              <td>
                <span className="inline-flex items-center gap-1.5">
                  <span className={clsx('status-dot', riskDotClass(row.riskRating))} />
                  <span className="font-mono text-[11px] text-stone-200">
                    {row.riskRating ?? NO_VALUE}
                  </span>
                  <DirectionMark direction={row.riskDirection} />
                </span>
              </td>
              <td className="max-w-[170px] truncate text-stone-400">
                {scope?.assetManagerEmail ?? NO_VALUE}
              </td>
              <td className="whitespace-nowrap text-right font-mono text-[10.5px] text-stone-500">
                {fmtDate(row.lastUpdateAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  )

  // ── Per-tab 4x4 grids ─────────────────────────────────────────────────────
  const renderActiveGrid = () => {
    if (active === 'risk') {
      return (
        <DashboardGrid rows={4}>
          <GridCell panelId="portfolio-attention" panelLabel="Portfolio attention" col={2} row={2} colStart={1} rowStart={1}>
            {attentionPanel}
          </GridCell>
          <GridCell panelId="covenants-reporting" panelLabel="Covenants & reporting" col={2} row={2} colStart={3} rowStart={1}>
            {covenantsPanel}
          </GridCell>
          <GridCell panelId="watchlist-actions" panelLabel="Watchlist action plans" col={2} row={2} colStart={1} rowStart={3}>
            {watchlistPanel}
          </GridCell>
          <GridCell panelId="requests-inspections" panelLabel="Requests & inspections" col={2} row={2} colStart={3} rowStart={3}>
            {requestsPanel}
          </GridCell>
        </DashboardGrid>
      )
    }
    if (active === 'maturity') {
      return (
        <DashboardGrid rows={4}>
          <GridCell panelId="maturity-refinance" panelLabel="Maturity & refinance" col={2} row={4} colStart={1} rowStart={1}>
            {maturityPanel}
          </GridCell>
          <GridCell panelId="performance-movement" panelLabel="Performance movement" col={2} row={4} colStart={3} rowStart={1}>
            {performancePanel}
          </GridCell>
        </DashboardGrid>
      )
    }
    return (
      <DashboardGrid rows={4}>
        <GridCell panelId="monitoring-tiers" panelLabel="Monitoring tiers" col={4} row={1}>
          {tiersBand}
        </GridCell>
        <GridCell panelId="portfolio-surveillance" panelLabel="My portfolio surveillance" col={4} row={3}>
          {surveillancePanel}
        </GridCell>
      </DashboardGrid>
    )
  }

  return (
    <DashboardShell
      dashboardId="asset"
      dashboardView={active}
      kicker="Post-closing"
      title="Asset Management Workspace"
      subtitle={
        scope?.assetManagerEmail
          ? `${scope.assetManagerEmail} · ${scope.assets} monitored assets`
          : scope?.unassigned
            ? `Unassigned · ${scope.assets} monitored assets with no manager on record`
            : scope?.desk
              ? `${manager === DESK_SCOPE ? 'Whole book (manager default)' : 'Whole book (your portfolio is empty)'} · ${scope.assets} monitored assets${unmonitoredClause(scope)}`
              : 'Portfolio surveillance, covenants, borrower requests and maturities.'
      }
      partition={board.partition}
      dataUpdatedAt={board.dataUpdatedAt}
      isFetching={board.isFetching}
      error={error}
      onRetry={retry}
      filters={toolbar}
      tabs={<DashboardTabs tabs={TABS} active={active} onChange={setActive} />}
      fill
    >
      <div className="flex h-full min-h-0 gap-2">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <KpiStrip
            tiles={headline?.tiles ?? []}
            columnsClassName="grid-cols-3 md:grid-cols-5 2xl:grid-cols-9"
            isLoading={isLoading}
            className="flex-shrink-0"
          />
          <BookUniverseLine universe={universe} className="flex-shrink-0 px-1" />
          {active === 'portfolio' && queryToolbar}
          {renderActiveGrid()}
        </div>

        {inspectorOpen && selected && (
          <aside className="flex w-[360px] flex-shrink-0 flex-col overflow-y-auto scrollbar-thin border-l border-amber-400/[0.08]">
            <AssetInspector
              row={selected}
              covenants={selectedCovenants}
              requests={selectedRequests}
              onClose={() => setInspectorOpen(false)}
            />
          </aside>
        )}
      </div>
    </DashboardShell>
  )
}
